import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Edit, Trash2, Eye, Mail, Phone, MapPin, CreditCard, Globe, AlertCircle, Download } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/context/AuthContext";
import { resolveFileUrl, downloadFile } from "@/api/base44Client";

export default function ClientCard({ client, purchaseCount, totalSpent, onEdit, onDelete, isDefaulted }) {
  const { canEdit, canExport } = useAuth();
  const [showIdView, setShowIdView] = useState(false);
  const [idDisplayUrl, setIdDisplayUrl] = useState(null);
  const [idLoadError, setIdLoadError] = useState(null);

  const isPdf = (url) => !url ? false : url.toLowerCase().includes('application/pdf') || (typeof url === 'string' && url.toLowerCase().endsWith('.pdf'));

  // Use direct URL for viewing (avoids fetch/CORS issues); data: URLs used as-is
  useEffect(() => {
    if (!showIdView || !client?.id_picture_url) {
      if (!showIdView) {
        setIdDisplayUrl(null);
        setIdLoadError(null);
      }
      return;
    }
    const rawUrl = client.id_picture_url;
    if (rawUrl.startsWith('data:')) {
      setIdDisplayUrl(rawUrl);
      setIdLoadError(null);
      return;
    }
    setIdDisplayUrl(resolveFileUrl(rawUrl));
    setIdLoadError(null);
  }, [showIdView, client?.id_picture_url]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="hover:shadow-lg transition-all duration-300 border-0 shadow-md">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            {/* Client Info */}
            <div className="flex items-start gap-4 flex-1">
              <div className="w-14 h-14 bg-gradient-to-br from-slate-900 to-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-bold text-amber-400">
                  {client.full_name?.charAt(0).toUpperCase() || 'C'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-slate-900">{client.full_name}</h3>
                  {isDefaulted && (
                    <Badge className="bg-red-100 text-red-700 border-red-200 border flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Defaulted Client
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600">
                  {client.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{client.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{client.phone}</span>
                  </div>
                  {client.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{client.address}</span>
                    </div>
                  )}
                  {client.nationality && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <span>{client.nationality}</span>
                    </div>
                  )}
                  {client.identification_number && (
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-slate-400" />
                      <span>ID: {client.identification_number}</span>
                      {client.id_picture_url && (
                        <button
                          type="button"
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                          onClick={(e) => { e.stopPropagation(); setShowIdView(true); }}
                        >
                          (View)
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Stats & Actions */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full md:w-auto">
              <div className="grid grid-cols-2 gap-4 w-full md:w-auto">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-900">{purchaseCount}</p>
                  <p className="text-xs text-slate-600">Purchases</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-600">
                    {totalSpent.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-600">{client.preferred_currency}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Link to={createPageUrl(`ClientProfile?id=${client.id}`)}>
                  <Button variant="outline" size="icon" className="hover:bg-slate-100">
                    <Eye className="w-4 h-4" />
                  </Button>
                </Link>
                {canEdit && (
                  <>
                    <Button variant="outline" size="icon" onClick={() => onEdit(client)} className="hover:bg-slate-100">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => onDelete(client)} className="hover:bg-red-50">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showIdView} onOpenChange={setShowIdView}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>ID/Passport Document</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto border rounded-lg bg-slate-50">
            {idLoadError ? (
              <div className="p-8 text-center text-slate-600">
                <p className="font-medium">{idLoadError}</p>
                <p className="text-sm mt-2">The file may have been removed or the server is unavailable.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => {
                    if (client?.id_picture_url && !client.id_picture_url.startsWith('data:')) {
                      window.open(resolveFileUrl(client.id_picture_url), '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  Try opening in new tab
                </Button>
              </div>
            ) : idDisplayUrl ? (
              isPdf(client?.id_picture_url) ? (
                <iframe
                  src={idDisplayUrl}
                  title="ID Document"
                  className="w-full min-h-[70vh] border-0"
                />
              ) : (
                <img
                  src={idDisplayUrl}
                  alt="ID Document"
                  className="w-full h-auto max-h-[70vh] object-contain"
                  onError={() => {
                    setIdDisplayUrl(null);
                    setIdLoadError('Failed to load');
                  }}
                />
              )
            ) : (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
                <p className="text-sm text-slate-500 mt-2">Loading document...</p>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowIdView(false)}>
              Close
            </Button>
            {canExport && client?.id_picture_url && (
              <Button
                type="button"
                onClick={async () => {
                  const url = resolveFileUrl(client.id_picture_url);
                  const ext = isPdf(client.id_picture_url) ? 'pdf' : 'png';
                  const filename = `id-document-${client.full_name?.replace(/\s+/g, '-') || 'client'}.${ext}`;
                  await downloadFile(url, filename);
                }}
                className="bg-gradient-to-r from-slate-900 to-slate-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}